import React, { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { useWizard } from 'ui-components/components/wizard/context';
import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import { Providers } from '../../../../../../enums/providers';
import { useFsuiContext } from '../../../../../../financial-services-ui/fsui-context-provider';
import { MINIMUM_PURCHASE_AMT } from '../../../../../../pages/settings/payment-and-subscription/components/constants/add-funds-constants';
import { useAddFundsContext } from '../../../../../../pages/settings/payment-and-subscription/context/add-funds-context-provider';
import { useShouldAutoInsure } from '../../../../../../services/insurance-service';
import { getAccountInformation } from '../../../../../../store/account-information/account-information.actions';
import { postWalletRegistrationSuccess } from '../../../../../../store/wallet/wallet.actions';
import { Workflows, EventSourceStrings } from '../../../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../../tracking/use-workflow-tracking';
import { trackWalletInsureAll } from '../../../../../../wallet/tracking-events';
import { messages } from '../../../../../messages';
import { fundAccount } from '../../../../../utilities/api';
import { PAYMENT_TAB, FORM_REQUIREMENTS, STAMPS_BAD_CREDENTIALS_REGEX } from '../../../../../utilities/constants';
import { useWalletRegistrationErrorContext } from '../../../contexts/wallet-registration-error-context';
import { useWalletRegistrationFormContext } from '../../../contexts/wallet-registration-form-context';
import { useWalletRegistrationModalContext } from '../../../contexts/wallet-registration-modal-context';
import { usePrepareSubmissionData } from '../../../hooks/use-prepare-submission-data';

const errorMessages = defineMessages({
  originalErrorMessage: {
    description: 'Original payment error message from back end',
    defaultMessage:
      "We're sorry, but your attempted purchase was not successful. Please verify that your payment method and billing information is correct. If the issue persists, please contact support@shipstation.com so we can help resolve this issue.",
    id: 'gFEEJO',
  },
  newErrorMessage: {
    description: 'New payment error message with directions to user on how to update payment information',
    defaultMessage:
      "We're sorry, but your attempted purchase was not successful. Please verify that your payment method and billing information are correct via the Carrier Settings link below. If the issue persists, please contact support@shipstation.com so we can help resolve the issue.",
    id: 'PEaAM9',
  },
});

export const AddFundsButton = () => {
  const {
    setSubmitting,
    submitting,
    fundAmount,
    updateAutoFundsConfig,
    autoFundEnabled,
    balanceThreshold,
    autoFundAmount,
    maxDailyFundings,
    orderRates,
    isSurchargeLoading,
    previewError,
  } = useAddFundsContext();
  const { errors, setErrors } = useWalletRegistrationErrorContext();
  const { walletForm, submitRegistration, postWalletRegistrationSuccessPayload } = useWalletRegistrationFormContext();
  const {
    closeWalletModal,
    labelPurchaseCallback,
    walletToRegister,
    selectedFulfillmentPlanIds,
    shouldAutoInsure,
    shouldShowInsuranceToggle,
    shouldShowOldTermsTab,
    shouldShowAdyenDropInComponent,
  } = useWalletRegistrationModalContext();
  const { jumpToStep } = useWizard();
  const { createPaymentSession } = useFsuiContext();
  const { prepareSubmissionData } = usePrepareSubmissionData();
  const isFromCreateLabel = selectedFulfillmentPlanIds.length > 0;
  const trackingId = EventSourceStrings.ADD_FUNDS_WALLET_REGISTRATION_STEP;
  const trackAddFunds = useTrackWorkflow(Workflows.ADD_FUNDS_FLOW);
  const intl = useIntl();

  const formattedAmount = intl.formatNumber(fundAmount, {
    style: 'currency',
    currency: walletToRegister.currencyCode,
  });

  const ButtonText = () => {
    if (isFromCreateLabel) {
      return <FormattedMessage {...messages.addFundsAndCreateLabelButton} />;
    }
    return (
      <FormattedMessage
        {...messages.fundAmount}
        values={{
          amount: formattedAmount,
        }}
      />
    );
  };
  const minimumLabelAmount = orderRates?.value;
  const minimumAmount = minimumLabelAmount < MINIMUM_PURCHASE_AMT ? MINIMUM_PURCHASE_AMT : minimumLabelAmount;

  // Check if UPS terms are accepted in new flow
  const upsTermsAccepted = walletForm?.[FORM_REQUIREMENTS.METADATA]?.[FORM_REQUIREMENTS.UPS_TERMS];
  const { newLabelProviderIds = [] } = walletToRegister;
  const isUpsWalletEligible = newLabelProviderIds.includes(Providers.UPSWallet.labelProviderId);
  const needsUpsTermsAcceptance = !shouldShowOldTermsTab && isUpsWalletEligible && !upsTermsAccepted;

  const disabled =
    submitting ||
    isSurchargeLoading ||
    previewError ||
    fundAmount < minimumAmount ||
    (autoFundEnabled && autoFundAmount < MINIMUM_PURCHASE_AMT) ||
    needsUpsTermsAcceptance;

  const { walletId } = walletToRegister;
  const dispatch = useDispatch();
  const amount = useMemo(
    () => ({
      value: Number(fundAmount),
      code: walletToRegister.currencyCode,
    }),
    [fundAmount, walletToRegister.currencyCode],
  );

  const showSuccessNotification = () => {
    NotificationService.showNotification({
      notificationType: NOTIFICATION_TYPE_SUCCESS,
      title: <FormattedMessage {...messages.addFundsSuccessTitle} />,
      content: <FormattedMessage {...messages.addFundsSuccessContent} />,
      duration: NOTIFICATION_DURATIONS.SHORT,
    });
  };
  const { newInsuranceProviderIds = [] } = walletToRegister;
  const { setShouldAutoInsure } = useShouldAutoInsure(newInsuranceProviderIds[0]);

  useEffect(() => {
    trackAddFunds(WorkflowSteps.START, { source: trackingId });
  }, [trackAddFunds, trackingId]);

  /**
   * Handles the actual fund account operation
   */
  const handleAddFunds = useCallback(async () => {
    try {
      await fundAccount(walletId, amount).finally(() => {
        dispatch(getAccountInformation());
      });
      trackAddFunds(WorkflowSteps.COMPLETE, {
        walletId,
        amount: amount.value,
        currency: amount.code,
      });
      showSuccessNotification();
      if (autoFundEnabled) {
        await updateAutoFundsConfig(autoFundEnabled, balanceThreshold, autoFundAmount, maxDailyFundings);
      }
      // Tracking the insure-all selection. Only non updates at this point
      if (shouldShowInsuranceToggle) {
        trackWalletInsureAll(walletId, false, shouldAutoInsure);
        if (shouldAutoInsure) {
          await setShouldAutoInsure(true);
        }
      }
      labelPurchaseCallback();
      dispatch(postWalletRegistrationSuccess());
      closeWalletModal();
    } catch (e) {
      const paymentError = e?.response?.data?.Errors.filter((error) => error.Message === errorMessages.originalErrorMessage.defaultMessage);
      const isPaymentError = paymentError?.length > 0;
      if (isPaymentError) {
        paymentError[0].ErrorContext.message = errorMessages.newErrorMessage.defaultMessage;
        paymentError[0].Message = errorMessages.newErrorMessage.defaultMessage;
        setErrors(paymentError);
      } else {
        setErrors(e?.response?.data?.Errors ?? [{}]);
      }
      trackAddFunds(WorkflowSteps.ERROR, {
        errors,
        walletId,
        isPaymentError,
        amount: amount.value,
        currency: amount.code,
        errorMessage: e?.message,
        ...(errors ?? []).reduce((acc, error, index) => {
          return {
            ...acc,
            [`error${index}Message`]: error?.Message,
          };
        }, {}),
      });
    }
  }, [
    autoFundEnabled,
    walletId,
    shouldAutoInsure,
    shouldShowInsuranceToggle,
    labelPurchaseCallback,
    dispatch,
    closeWalletModal,
    amount,
    updateAutoFundsConfig,
    balanceThreshold,
    autoFundAmount,
    maxDailyFundings,
    setShouldAutoInsure,
    errors,
    setErrors,
    trackAddFunds,
  ]);

  /**
   * Handles wallet registration for the new flow (no Terms tab).
   * Returns true if registration succeeded, false otherwise.
   */
  const handleRegistration = useCallback(async () => {
    setErrors([]);

    const { submissionData } = prepareSubmissionData();
    const { errors: registrationErrors } = await submitRegistration(submissionData, false);

    if (registrationErrors) {
      // Refresh providers/wallets in case registration was partially successful
      dispatch(postWalletRegistrationSuccess(postWalletRegistrationSuccessPayload));
      setErrors(registrationErrors);

      const { Message = '' } = registrationErrors[0] || {};
      if (Message.match(STAMPS_BAD_CREDENTIALS_REGEX)) {
        jumpToStep(PAYMENT_TAB);
        return false;
      }

      if (shouldShowAdyenDropInComponent) {
        createPaymentSession(true);
      }
      jumpToStep(PAYMENT_TAB);
      return false;
    }

    return true;
  }, [
    setErrors,
    prepareSubmissionData,
    submitRegistration,
    shouldShowAdyenDropInComponent,
    createPaymentSession,
    jumpToStep,
    dispatch,
    postWalletRegistrationSuccessPayload,
  ]);

  /**
   * Main submit handler - branches based on flow type
   */
  const onSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      if (!shouldShowOldTermsTab) {
        // New flow: Register wallet first, then add funds
        const registrationSucceeded = await handleRegistration();
        if (!registrationSucceeded) {
          return;
        }
      }
      // Either old flow (wallet already registered) or new flow registration succeeded
      await handleAddFunds();
    } finally {
      setSubmitting(false);
    }
  }, [setSubmitting, shouldShowOldTermsTab, handleRegistration, handleAddFunds]);

  return (
    <Button disabled={disabled} onClick={onSubmit} theme='primary'>
      <ButtonText />
    </Button>
  );
};
